import { GoogleLogin } from '@react-oauth/google';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
    const { loginWithGoogle } = useAuth();

    const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
        if (!credentialResponse.credential) return;
        try {
            await loginWithGoogle(credentialResponse.credential);
            onClose();
        } catch (err) {
            console.error('Google sign-in error:', err);
        }
    };

    return (
        <div
            className={`fixed inset-0 z-[1100] ${isOpen ? '' : 'pointer-events-none'}`}
            aria-hidden={!isOpen}
        >
            {/* Overlay */}
            <div
                className={`absolute inset-0 bg-black/10 backdrop-blur-[2px] transition-opacity duration-300 ${
                    isOpen ? 'opacity-100' : 'opacity-0'
                }`}
                onClick={onClose}
            />

            {/* Drawer */}
            <aside
                className={`absolute right-0 top-0 h-full w-full max-w-[360px] transform border-l border-black/5 bg-white shadow-[0_0_40px_rgba(0,0,0,0.08)] transition-transform duration-300 ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
                role="dialog"
                aria-modal="true"
                aria-label="Sign in"
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 rounded-md p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Close"
                >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Content */}
                <div className="flex h-full flex-col px-8 pt-24">
                    <div className="mb-16">
                        <h2 className="mb-2 text-2xl font-semibold text-gray-900">
                            Welcome to CourseMap
                        </h2>
                        <p className="text-sm text-gray-500">
                            Sign in to track your progress and build your schedule.
                        </p>
                    </div>

                    <div>
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={() => console.error('Google sign-in failed')}
                            useOneTap={false}
                            text="continue_with"
                            shape="rectangular"
                            size="large"
                            width="296"
                        />
                        <p className="mt-4 text-xs text-gray-400 leading-relaxed">
                            By continuing, you agree to our{' '}
                            <Link to="/terms" onClick={onClose} className="underline hover:text-gray-600">Terms of Service</Link>
                            {' '}and{' '}
                            <Link to="/privacy" onClick={onClose} className="underline hover:text-gray-600">Privacy Policy</Link>.
                        </p>
                    </div>
                </div>
            </aside>
        </div>
    );
}